import { logger } from '@tunasong/models'
import type { Y } from '@tunasong/sync-lib'
import { IndexeddbPersistence } from '@tunasong/sync-lib'
import { useCallback, useEffect, useRef, useState } from 'react'
import invariant from 'tiny-invariant'
import { indexeddbCacheName } from './db.js'

interface useYjsDBSyncProps {
  entityId: string
  doc: Y.Doc
  disabled?: boolean
}
/** Sync from IndexedDB */
export const useYjsDBSync = (props: useYjsDBSyncProps) => {
  const { doc, entityId, disabled = false } = props

  invariant(entityId, `useYjsDBSync without entityId specified`)
  const markAsReady = useRef<(() => void) | null>(null)
  const readyPromise = useRef<Promise<void> | null>(null)
  useEffect(() => {
    readyPromise.current = new Promise(resolve => {
      markAsReady.current = resolve
    })
  }, [])

  /** `true` when the doc has been synced with local persistence, or syncing is disabled */
  const [isSynced, setIsSynced] = useState(disabled ? true : false)
  const idbRef = useRef<IndexeddbPersistence | null>(null)

  /** Start provider first to ensure that the SyncHandler will record changes from Indexeddb */
  useEffect(() => {
    if (disabled) {
      return
    }

    const idb = new IndexeddbPersistence({ cacheName: indexeddbCacheName(entityId), doc })
    const onSynced = () => {
      logger.debug(`useYjsDBSync: Synced with local IndexedDB!`)
      setIsSynced(true)
    }

    idb.on('synced', onSynced)
    idbRef.current = idb

    invariant(markAsReady.current, `useYjsDBSync: markAsReady.current is not set`)
    markAsReady.current()

    logger.debug(`useYjsDBSync: marking as ready`)

    /** Cleanup when closing */
    return () => {
      idb.off('synced', onSynced)
      // eslint-disable-next-line no-underscore-dangle
      if (idb._storeTimeoutId) {
        logger.debug(`useYjsDBSync: Pending database update. Not destroying the database.`)
      } else {
        void idb.destroy()
      }
    }
  }, [disabled, doc, entityId])

  /**
   * Sync from local IndexedDB store to doc
   */
  const sync = useCallback(async () => {
    invariant(markAsReady.current, `useYjsDBSync: markAsReady.current is not set`)
    logger.debug(`useYjsDBSync: waiting for IndexedDB sync to be ready...`, { promise: readyPromise.current })
    await readyPromise.current
    invariant(idbRef.current, `useYjsDBSync: idbRef.current is not set`)

    logger.debug(`useYjsDBSync: starting sync with local IndexedDB...`)

    return idbRef.current.sync()
  }, [])

  return { isSynced, sync }
}
